import has from 'lodash/has';

import {
  SearchCombo,
  SearchQuery,
  DataTypeSearchCombo,
  SearchQueryField,
  GeoSearchField,
  MultiValueSearchField,
} from 'modules/workflows/types';
import * as fieldTypes from 'utils/fieldTypes';
import { WalkSearchQueryQueryTypes } from './walkSearchQueries.constants';
import { Level } from './walkSearchQueries.types';

/** Is the type of query a {@link SearchCombo}? */
export const isSearchCombo = (query: SearchQuery): query is SearchCombo =>
  has(query, 'combinator') || has(query, 'searchQueries');

/** Is the type of query a {@link DataTypeSearchCombo}? */
export const isDataTypeSearchCombo = (query: SearchQuery): query is DataTypeSearchCombo =>
  has(query, 'dataType') && has(query, 'searchCombo');

/** Is the type of query a {@link SearchQueryField}? */
export const isSearchQueryField = (query: SearchQuery): query is SearchQueryField =>
  has(query, 'field') || has(query, 'comparatorType') || has(query, 'value');

export const isMultiValueSearchField = (query: SearchQueryField): query is MultiValueSearchField =>
  has(query, 'values');

/** Is the type of query a {@link GeoSearchField}? */
export const isGeoSearchField = (query: SearchQueryField): query is GeoSearchField =>
  query.fieldType === fieldTypes.GEO_LOCATION ||
  // For some reason, the field type does not always exist.  If that is the
  // case, we check for the distance and location fields.
  // This was the cause of https://iterable.atlassian.net/browse/WF-5058
  !!(query as GeoSearchField).distance ||
  !!(query as GeoSearchField).location;

/**
 * Gets the type of the query.
 * @param {SearchQuery} query The query to get the type of.
 * @returns {WalkSearchQueryQueryTypes} The query type.
 */
export const getSearchQueryType = (query: SearchQuery): WalkSearchQueryQueryTypes => {
  if (isSearchCombo(query)) return WalkSearchQueryQueryTypes.SearchCombo;
  if (isDataTypeSearchCombo(query)) return WalkSearchQueryQueryTypes.DataTypeCombo;
  if (isSearchQueryField(query)) return WalkSearchQueryQueryTypes.Field;
  return WalkSearchQueryQueryTypes.Unknown;
};

/**
 * Is this the last level that should be checked?
 * @param {Level} currentLevel The current level of the query
 * @param {Level} maxLevel The max level allowed
 * @example
 * hasMaxLevelBeenReached(1) // false
 * hasMaxLevelBeenReached(1, 2) // false
 * hasMaxLevelBeenReached(2, 2) // true
 */
export const hasMaxLevelBeenReached = (currentLevel: Level, maxLevel: Level = Infinity) =>
  Number.isFinite(maxLevel) && currentLevel >= maxLevel;
